// The only way this app learns who is signed in. `AuthProvider.tsx` maps the
// three outcomes of `GET /api/auth/session` onto the three `AuthState` variants;
// nothing here reads a cookie or a token, because by design (§05) the browser
// never holds one.

import { useContext } from "react";
import { AuthContext } from "./AuthProvider.js";
import { principalHasRole, type AuthState, type CrmRole, type Principal } from "./principal.js";

export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (state === null) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return state;
}

/**
 * For components rendered only inside the authenticated branch of the shell.
 * Throwing rather than returning null keeps the "is someone signed in" decision
 * in ONE place (the shell) instead of re-litigated in every screen.
 */
export function usePrincipal(): Principal {
  const state = useAuth();
  if (state.status !== "authenticated") {
    throw new Error("usePrincipal used outside an authenticated subtree");
  }
  return state.principal;
}

/**
 * Every editing affordance in the app is gated on `crm_editor` through this
 * hook, so a role check is never written against `principal.roles` at a call
 * site. Controls are HIDDEN rather than disabled for a reader: a disabled
 * button says "you could do this if something changed", which is not what a
 * missing role means.
 */
export function useHasRole(role: CrmRole): boolean {
  const state = useAuth();
  return state.status === "authenticated" && principalHasRole(state.principal, role);
}
